// --------------------------------------------------------------
// Created On: 2021-10-14
// Author: Zachary Thomas
//
// Last Modified: 2026-07-17
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useMemo, useState } from "react";
import AlertMessageModal from "./AlertMessageModal/AlertMessageModal";
import { useNavigate } from "react-router-dom";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import useReduxSelector from "../../../hooks/useReduxSelector";
import { returnToOriginalCompany, returnToPreviousCompany } from "../../../redux/features/currentUserSlice";
import { setIdToken } from "../../../redux/features/idTokenSlice";
import { Link } from "react-router-dom";
import { RPM_COMPANY } from "../../../constants/miscellaneous";
import userHasPermission from "../../../utilities/userHasPermission";
import userHasCrossCompanyAccess from "../../../utilities/userHasCrossCompanyAccess";
import userHasCrossSubsidiaryAccess from "src/utilities/userHasCrossSubsidiaryAccess";
import { CREATE_ALERT_MESSAGES_PERMISSION } from "../../../constants/permissions";
import styles from "./UserOptions.module.scss";

// User dropdown for managing settings and logging out.
export default function UserOptions(): Component {
  const [showModal, setShowModal] = useState(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();
  const previousCompanyName = useMemo(() => getPreviousCompanyName(), [JSON.stringify(currentUser.userCompanyPath)]);

  // Gets the previous company name or null if there is no previous company.
  function getPreviousCompanyName(): string | null {
    if (currentUser.userCompanyPath !== undefined) {
      if (currentUser.userCompanyPath.length > 1) {
        return currentUser.userCompanyPath[currentUser.userCompanyPath.length - 2].companyName;
      } else if (currentUser.userCompanyPath.length === 1) {
        return currentUser.originalCompanyName;
      }
    }
    return null;
  }

  // Exit the current company and return to the company that is home to the current user.
  function handleReturnToOriginalCompany(): void {
    dispatch(returnToOriginalCompany());
    if (currentUser.originalCompanyName === RPM_COMPANY) {
      navigate("/", { replace: true });
    } else {
      navigate("/manage/subsidiaries", { replace: true });
    }
  }

  // Exit the current company and return to the previous company the current user was in.
  function handleReturnToPreviousCompany(): void {
    dispatch(returnToPreviousCompany());
    if (previousCompanyName === RPM_COMPANY) {
      navigate("/", { replace: true });
    } else {
      navigate("/manage/subsidiaries", { replace: true });
    }
  }

  // Logout the current user.
  function logout(): void {
    setIdToken(null);
    dispatch(returnToOriginalCompany());
    navigate("/login");
  }

  return (
    <div className={`${styles.dropdown} dropdown`}>
      {/* Button for selecting user options. */}
      <button
        className={`${styles.container} btn btn-primary py-0`}
        type="button"
        id="user-navbar-button"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        data-test="navbar-user-options-button"
      >
        <div className="d-flex align-items-center" aria-labelledby="user-navbar-button">
          <div className={`${styles.userNavbarText}`}>
            <div className={styles.fullContent}>
              <div className={`${styles.bigUserNameText} my-2`}>{currentUser.userName}</div>
            </div>

            <div className={styles.smallContent}>
              <i className="fa fa-fw fa-cog" />
            </div>
          </div>
        </div>
      </button>

      {/* Dropdown menu of options */}
      <div
        className={`${styles.dropdownMenu} dropdown-menu dropdown-menu-end`}
        data-test="navbar-user-options-dropdown"
      >
        {currentUser.userCompanyPath !== undefined && (userHasCrossCompanyAccess() || userHasCrossSubsidiaryAccess()) && (
          <Fragment>
            {currentUser.userCompanyPath.length > 1 && (
              <button
                data-test="user-options-return-to-original-company-button"
                className={`${styles.dropdownItem} d-inline-block d-lg-none dropdown-item`}
                onClick={() => handleReturnToOriginalCompany()}
              >
                Return to &apos;
                {currentUser.originalCompanyName}&apos;
              </button>
            )}
            {currentUser.userCompanyPath.length > 0 && (
              <button
                className={`${styles.dropdownItem} d-inline-block d-lg-none dropdown-item`}
                data-test="user-options-return-to-previous-company-button"
                onClick={() => handleReturnToPreviousCompany()}
              >
                Return to &apos;{previousCompanyName}&apos;
              </button>
            )}
          </Fragment>
        )}

        <Link to="/account/user-info" className="router-link">
          <button className={`${styles.dropdownItem} dropdown-item`} data-test="edit-user-account-info-button">
            Edit Account Info
          </button>
        </Link>

        <Link to="/account/password" className="router-link">
          <button className={`${styles.dropdownItem} dropdown-item`} data-test="change-password-button">
            Change Password
          </button>
        </Link>

        {userHasPermission([[CREATE_ALERT_MESSAGES_PERMISSION]]) && currentUser.companyName !== RPM_COMPANY && (
          <button className={`${styles.dropdownItem} dropdown-item`} onClick={() => setShowModal(true)}>
            Send Alert Message
          </button>
        )}

        <button className={`${styles.dropdownItem} dropdown-item`} data-test="logout-button" onClick={() => logout()}>
          Sign Out
        </button>
      </div>

      <AlertMessageModal showModal={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
}
